// src/components/CreateProjectButton.tsx

/**
 * Project creation action for the Quantik Mind enterprise frontend.
 *
 * This component is responsible for:
 * - opening the project creation dialog
 * - validating the project name input
 * - submitting the project creation request to the backend
 * - reporting loading and error states to the user
 * - notifying the parent component when a new project is created
 */

import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useSubscription } from '../context/SubscriptionContext';
import { api } from '../utils/apiClient';
import { formatApiError } from '../utils/formatApiError';
import ActionButton from './common/ActionButton';

/**
 * Minimal project payload returned after successful creation.
 */
interface CreatedProject {
  /** Unique identifier of the newly created project. */
  id: string;

  /** Human-readable name of the newly created project. */
  name: string;
}

/**
 * Props accepted by the project creation button component.
 */
interface CreateProjectButtonProps {
  /**
   * Callback invoked after a project is successfully created.
   *
   * @param projectId - Identifier of the newly created project.
   * @param projectName - Display name of the newly created project.
   */
  onProjectCreated: (projectId: string, projectName: string) => void;
}

/**
 * Render the project creation action and dialog.
 *
 * @param props - Component props.
 * @returns Project creation button with modal dialog.
 */
const CreateProjectButton: React.FC<CreateProjectButtonProps> = ({
  onProjectCreated,
}) => {
  const { refreshSubscription } = useSubscription();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Open the dialog and reset transient form state.
   */
  const handleOpen = () => {
    setOpen(true);
    setName('');
    setError(null);
  };

  /**
   * Close the dialog unless a request is currently in progress.
   */
  const handleClose = () => {
    if (!loading) {
      setOpen(false);
    }
  };

  /**
   * Submit the project creation request to the backend.
   */
  const handleCreate = async () => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      setError('Project name is required.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await api.post<CreatedProject>('/projects/', {
        name: trimmedName,
      });

      await refreshSubscription();
      onProjectCreated(data.id, data.name);
      setOpen(false);
    } catch (creationError: any) {
      setError(formatApiError(creationError, 'Failed to create project.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ActionButton
        fullWidth
        startIcon={<AddIcon />}
        onClick={handleOpen}
        sx={{
          mb: 0,
          mt: 0,
          fontSize: '0.8125rem',
        }}
      >
        Create Project
      </ActionButton>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Project</DialogTitle>

        <DialogContent>
          {error && (
            <Stack
              sx={{
                mb: 2,
                borderRadius: 3,
                background: 'rgba(255, 190, 92, 0.08)',
                border: '1px solid rgba(255, 190, 92, 0.18)',
              }}
            >
              <Typography variant="body2" sx={{ color: '#F5D7A1', p: 2 }}>
                {error}
              </Typography>
            </Stack>
          )}

          <TextField
            autoFocus
            label="Project Name"
            fullWidth
            value={name}
            onChange={(event) => setName(event.target.value)}
            margin="normal"
            required
            disabled={loading}
            placeholder="e.g. Banking App Production"
            onKeyDown={(event) => {
              if (event.key === 'Enter' && name.trim()) {
                void handleCreate();
              }
            }}
          />
        </DialogContent>

        <DialogActions>
          <ActionButton
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </ActionButton>

          <ActionButton
            onClick={() => void handleCreate()}
            disabled={loading || !name.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Create'}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateProjectButton;
